import { escape, BRAND, buildSponsorStrip } from "./reelShared";

/**
 * Monthly Recap Reel — 1080×1920 vertical, community-level.
 * Not personal — this is the EVIQ community's monthly outcomes reel,
 * shareable to any social feed and perfect for sponsor underwriting.
 */
export const buildMonthlyRecapReelSVG = ({ community, color = BRAND.accent, sponsor, month, year }) => {
    const w = 1080;
    const h = 1920;
    const cx = w / 2;
    const safeTop = 300;

    const nowMonth =
        month ||
        new Date().toLocaleDateString(undefined, { month: "long", timeZone: "UTC" }).toUpperCase();
    const nowYear = year || new Date().getUTCFullYear();

    const trees = Math.floor(community?.trees_restored ?? 0).toLocaleString();
    const plastic = Math.floor(community?.ocean_plastic_lbs ?? 0).toLocaleString();
    const batteries = Math.floor(community?.batteries_diverted ?? 0).toLocaleString();
    const schools = Math.floor(community?.schools_supported ?? 0).toLocaleString();
    const veterans = Math.floor(community?.veterans_sponsored ?? 0).toLocaleString();

    const sponsorSvg = sponsor ? buildSponsorStrip({ sponsor, cx, y: 1620, color }) : "";
    const bottomCaption = sponsor
        ? ""
        : `<text x="${cx}" y="1680" text-anchor="middle" fill="${BRAND.inkFaint}" font-family="'Helvetica Neue', Arial, sans-serif" font-size="22" letter-spacing="7">POWERED BY THE EVIQ COMMUNITY</text>`;

    // Metric row helper: label above, big value below, gradient underline.
    const row = (label, value, y, accent) => `
    <text x="${cx}" y="${y}" text-anchor="middle" fill="${BRAND.inkDim}" font-family="'Helvetica Neue', Arial, sans-serif" font-size="24" font-weight="700" letter-spacing="8">${escape(label)}</text>
    <text x="${cx}" y="${y + 100}" text-anchor="middle" fill="${BRAND.ink}" font-family="Georgia, 'Times New Roman', serif" font-size="120" font-weight="700" letter-spacing="-2">${escape(value)}</text>
    <line x1="${cx - 80}" y1="${y + 130}" x2="${cx + 80}" y2="${y + 130}" stroke="${accent}" stroke-width="2"/>`;

    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" role="img" aria-label="EVIQ monthly community recap reel">
  <defs>
    <linearGradient id="mr-bg" x1="0" y1="0" x2="0" y2="1">
      <stop offset="0%" stop-color="${BRAND.bgTop}"/>
      <stop offset="100%" stop-color="${BRAND.bgBottom}"/>
    </linearGradient>
    <radialGradient id="mr-glow" cx="50%" cy="25%" r="65%">
      <stop offset="0%" stop-color="${color}" stop-opacity="0.24"/>
      <stop offset="100%" stop-color="${color}" stop-opacity="0"/>
    </radialGradient>
  </defs>
  <rect width="${w}" height="${h}" fill="url(#mr-bg)"/>
  <rect width="${w}" height="${h}" fill="url(#mr-glow)"/>

  <!-- Brand header -->
  <g transform="translate(80 ${safeTop})">
    <rect x="0" y="0" width="52" height="52" rx="10" fill="#0a0a0a" stroke="#27272a"/>
    <path d="M 16 12 L 32 12 L 26 28 L 36 28 L 20 48 L 26 30 L 14 30 Z" fill="${color}"/>
    <text x="72" y="22" fill="${BRAND.inkDim}" font-family="'Helvetica Neue', Arial, sans-serif" font-size="16" font-weight="700" letter-spacing="6">EVIQ</text>
    <text x="72" y="46" fill="${BRAND.ink}" font-family="Georgia, serif" font-style="italic" font-size="26">Earth Report</text>
  </g>

  <text x="${cx}" y="${safeTop + 150}" text-anchor="middle" fill="${color}" font-family="'Helvetica Neue', Arial, sans-serif" font-size="26" font-weight="700" letter-spacing="10">${escape(nowMonth)}  ·  ${escape(nowYear)}</text>
  <text x="${cx}" y="${safeTop + 240}" text-anchor="middle" fill="${BRAND.ink}" font-family="Georgia, serif" font-size="72" font-weight="700">This month, together</text>
  <text x="${cx}" y="${safeTop + 314}" text-anchor="middle" fill="${BRAND.inkDim}" font-family="Georgia, serif" font-size="60" font-style="italic">we restored the planet.</text>

  <g>
    ${row("TREES RESTORED", trees, safeTop + 430, color)}
    ${row("LBS OCEAN PLASTIC REMOVED", plastic, safeTop + 640, BRAND.ocean)}
    ${row("BATTERIES DIVERTED", batteries, safeTop + 850, BRAND.violet)}
    ${row("SCHOOLS SUPPORTED · VETERANS SPONSORED", `${schools} · ${veterans}`, safeTop + 1060, BRAND.solar)}
  </g>

  ${sponsorSvg}
  ${bottomCaption}
</svg>`;
};
